import { Button, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Typography } from '@mui/material'
import { DatePicker, TextField as TextFieldCustom } from '@e3dc-react/shell/Elements/InputFields'
import React, { useEffect, useState } from 'react'
import { BatteryExportModule, BatteryJob, BatteryType, DcbhistoryModule } from './BatteryExportUtils'
import moment from '@e3dc-react/shell/Libs/moment'
import { makeStyles } from 'tss-react/mui'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import BatteryDcbistoryList from './BatteryDbchistoryList'
import CloseIcon from '@mui/icons-material/Close'
import { ParseDetailsReturnType } from '@e3dc-react/shell/Hooks/useValidation/useValidation'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface StyleProps {
  isDisableDeleteJob: boolean
}
const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  rootContainer: {
    position: 'relative',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
  },
  dcbhistoryList: {
    justifyItems: 'start',
    marginTop: theme.spacing(4),
    paddingRight: theme.spacing(2),
    width: '100%',
  },
  removeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  infoBatteryExportText: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.light,
  },
}))

interface BatteryJobProps {
  index: number
  removeJob: (index: number) => void
  batteryExport: BatteryExportModule
  setBatteryExport: (batteryExport: BatteryExportModule) => void
  isDisableDeleteJob: boolean
  validationActive: boolean
  validation: ParseDetailsReturnType
  isValidJob: boolean
  setIsValidJob: (isValidJob: boolean) => void
}

const BatteryJobComponent: React.VFC<BatteryJobProps> = ({
  index,
  removeJob,
  batteryExport,
  setBatteryExport,
  isDisableDeleteJob,
  validationActive,
  validation,
  isValidJob,
  setIsValidJob,
}) => {
  const { classes } = useStyles({ isDisableDeleteJob })
  const authFetch = useAuthFetchWithResult()
  const { t } = useCustomTranslation()

  const [type, setType] = useState<BatteryType>(BatteryType.SN)
  const [serialNo, setSerialNo] = useState<string>()
  const [startDate, setStartDate] = useState<moment.Moment | null>()
  // const startDate = batteryExport?.jobs?.[index].from
  const [endDate, setEndDate] = useState<moment.Moment | null>()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [dcbhistory, setDcbhistory] = useState<DcbhistoryModule[]>()
  const [dcbHistoryIdxsToInclude, setDcbHistoryIdxsToInclude] = useState<boolean[]>(Array(dcbhistory?.length).fill(false))

  const onLoadDcbhistory = (): void => {
    authFetch<DcbhistoryModule[]>({
      path: `csv-battery-archive/dcb-history/${type}/${type === 'tc' ? serialNo : parseInt(serialNo ?? '0')}`,
      payload: { from: moment(startDate, 'DD:MM:YYYY').valueOf(), to: moment(endDate, 'DD:MM:YYYY').valueOf() },
      endpoint: process.env.REACT_APP_CSV_ARCHIVE_API_ENDPOINT,
    })
      .then(res => {
        setDcbhistory(res)
      })
      .catch(err => console.error(err))
  }

  useEffect(() => {
    if (startDate) onUpdateJob(startDate, 'from')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate])

  useEffect(() => {
    if (endDate) onUpdateJob(endDate, 'to')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate])

  useEffect(() => {
    if (type) onUpdateJob(type, 'type')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  useEffect(() => {
    if (serialNo) onUpdateJob(parseInt(serialNo), 'id')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNo])

  useEffect(() => {
    const dcbHistoryToInclude: DcbhistoryModule[] = []
    if (dcbhistory) {
      dcbhistory.forEach((history, idx) => {
        if (dcbHistoryIdxsToInclude[idx]) {
          dcbHistoryToInclude.push(history)
        }
      })
    }
    onUpdateJob(dcbHistoryToInclude, 'dcb_history')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcbhistory, dcbHistoryIdxsToInclude])

  /**
   * Update the current job of type BatteryJobs
   * @param value updated value for the corresponding property
   * @param property the property name
   */
  const onUpdateJob = (value: string | moment.Moment | DcbhistoryModule[] | number, property: keyof BatteryJob): void => {
    if (batteryExport.jobs && value) {
      const updatedJobs = batteryExport.jobs.map((job, jobIndex) => {
        if (index === jobIndex) {
          return { ...job, [property]: property === 'from' || property === 'to' ? value.valueOf() : value }
        }
        return job
      })
      setBatteryExport({ ...batteryExport, jobs: updatedJobs })
    }
  }

  useEffect(() => {
    setIsValidJob(!(type === BatteryType.TC && !dcbHistoryIdxsToInclude.includes(true)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, dcbHistoryIdxsToInclude])

  return (
    <Grid container spacing={2} marginBottom={4} className={classes.rootContainer}>
      <IconButton
        className={classes.removeButton}
        onClick={() => removeJob(index)}
        disabled={batteryExport?.jobs?.length === undefined || batteryExport?.jobs?.length <= 1}
      >
        <CloseIcon fontSize={'small'} color={isDisableDeleteJob ? 'disabled' : 'secondary'} />
      </IconButton>
      <Grid item xs={12} paddingTop={2}>
        <Typography variant="h6">{`Job ${index}`}</Typography>
        <RadioGroup>
          <FormControlLabel
            label={t('batteryJob.systemSerialNo')}
            value={BatteryType.SN}
            checked={type === BatteryType.SN}
            control={<Radio color={'primary'} size="small" />}
            data-qa={'batteryExportModules-batteryJob-systemSerialNo'}
            onChange={() => setType(BatteryType.SN)}
          />
          <FormControlLabel
            label={t('batteryJob.batterySerialNo')}
            value={BatteryType.TC}
            checked={type === BatteryType.TC}
            control={<Radio color={'primary'} size="small" />}
            data-qa={'batteryExportModules-batteryJob-batterySerialNo'}
            onChange={() => setType(BatteryType.TC)}
          />
        </RadioGroup>
      </Grid>
      <Grid container spacing={2} padding={2} justifyContent={'space-between'}>
        <Grid item xs={12} md={6}>
          <TextFieldCustom
            label={'serial no'}
            value={serialNo}
            onChange={value => setSerialNo(value)}
            helperText={validation?.[`jobs.${index}.id`]?.message}
          />
        </Grid>
        <Grid item xs={5} md={3}>
          <DatePicker
            value={startDate ? moment(startDate, 'DD:MM:YYYY') : null} // null necessary for validation (otherwise field is marked in red until value is entered)
            onChange={value => setStartDate(value)}
            label={t('batteryJob.startDate')}
            data-qa={'batteryExportModules-batteryJob-startDate'}
            maxDate={endDate ? moment(endDate, 'DD:MM:YYYY').subtract(1, 'd') : moment()}
            helperText={validation?.[`jobs.${index}.from`]?.message}
          />
        </Grid>
        <Grid item xs={5} md={3}>
          <DatePicker
            value={endDate ? moment(endDate, 'DD:MM:YYYY') : null}
            onChange={value => setEndDate(value)}
            label={t('batteryJob.endDate')}
            data-qa={'batteryExportModules-batteryJob-endDate'}
            minDate={moment(startDate, 'DD:MM:YYYY').add(1, 'd')}
            maxDate={moment()}
            helperText={validation?.[`jobs.${index}.to`]?.message}
          />
        </Grid>
      </Grid>
      <Grid item paddingBottom={2}>
        <Button onClick={() => onLoadDcbhistory()} disabled={!type || !serialNo}>
          {t('batteryJob.dcbLoading')}
        </Button>
        {!isValidJob && validationActive && (
          <Typography className={classes.infoBatteryExportText} variant="body2">
            {'Beim Batterie Export (tcSerialCode) muss mindestens ein Eintrag der DCB-Historie ausgewählt werden.'}
          </Typography>
        )}
      </Grid>
      {dcbhistory && (
        <Grid item className={classes.dcbhistoryList}>
          <BatteryDcbistoryList
            data={dcbhistory}
            dcbHistoryIdxsToInclude={dcbHistoryIdxsToInclude}
            setDcbHistoryIdxsToInclude={setDcbHistoryIdxsToInclude}
          />
        </Grid>
      )}
    </Grid>
  )
}

export default BatteryJobComponent
